import { useEffect, useMemo, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { CustomPlanEditor } from './CustomPlanEditor';
import { DialogCommonFields } from './DialogCommonFields';
import { buildCustom, CustomPunchRow, groupByDate, HH_MM_RE, isDayEdited, listCustomDays, PUNCH_TYPES, sortPlan } from './punchPlan';
import { useCreatePunches } from './useCreatePunches';
import { CreatedBy, CreatePunchesRequest, CreatePunchesResponse, DayDataContext, DayEntry, MAX_PUNCHES_PER_REQUEST, PlannedPunch, PunchType } from '../types';
import { UUID_RE } from '../urlParams';

interface AddCustomDialogProps {
  open: boolean;
  range: { from: string; to: string };
  days: DayEntry[];
  context: DayDataContext;
  onClose: () => void;
  onCreate: (request: CreatePunchesRequest) => Promise<CreatePunchesResponse>;
}

const DEFAULT_ROWS: CustomPunchRow[] = [
  { time: '09:00', punchType: 'entry' },
  { time: '12:00', punchType: 'break_start' },
  { time: '13:00', punchType: 'break_end' },
  { time: '18:00', punchType: 'exit' }
];

const YYYY_MM_DD_RE = /^\d{4}-\d{2}-\d{2}$/;

export const outsideLoadedHint = (from: string, to: string) => `Dates must be inside the loaded range ${from} to ${to}. Load data for a wider range first.`;

export const TEMPLATE_CHANGED_HINT = 'Template or filters changed. Your per-day edits are kept until you click "Apply template to all days".';

export const INVALID_TIME_HINT = 'Fix the empty or invalid times in the plan.';

export const AddCustomDialog = ({ open, range: initialRange, days, context, onClose, onCreate }: AddCustomDialogProps) => {
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const [rows, setRows] = useState<CustomPunchRow[]>(DEFAULT_ROWS);
  const [skipDaysWithPunches, setSkipDaysWithPunches] = useState(true);
  const [weekdaysOnly, setWeekdaysOnly] = useState(true);
  const [createdBy, setCreatedBy] = useState<CreatedBy>('admin');
  const [requestedBy, setRequestedBy] = useState('');
  const [reason, setReason] = useState('');
  const requestedByInvalid = requestedBy !== '' && !UUID_RE.test(requestedBy);
  const { submitting, error, partial, submit } = useCreatePunches(onCreate, onClose);

  const templatePlan = useMemo(() => buildCustom({ from, to }, rows, { skipDaysWithPunches, weekdaysOnly }, days), [from, to, rows, skipDaysWithPunches, weekdaysOnly, days]);
  const customDays = useMemo(() => listCustomDays({ from, to }, { skipDaysWithPunches, weekdaysOnly }, days), [from, to, skipDaysWithPunches, weekdaysOnly, days]);

  const [plan, setPlan] = useState<PlannedPunch[]>(templatePlan);
  const [dirty, setDirty] = useState(false);
  // The rows that built the plan currently applied (on load, or via "Apply template to all days") - used
  // instead of the live `rows` for the "edited" marker, so adding/editing a template row while dirty does
  // not retroactively mark every untouched day as edited against a template it was never built from.
  const [appliedRows, setAppliedRows] = useState<CustomPunchRow[]>(rows);
  // The template plan last actually applied to `plan` (on load, or via "Apply template to all days") -
  // compared against the current templatePlan to know whether the template has drifted from an edited plan.
  const appliedTemplateRef = useRef<PlannedPunch[]>(templatePlan);

  useEffect(() => {
    if (!dirty) {
      setPlan(templatePlan);
      appliedTemplateRef.current = templatePlan;
      setAppliedRows(rows);
    }
  }, [templatePlan, dirty, rows]);

  const templateChanged = dirty && JSON.stringify(templatePlan) !== JSON.stringify(appliedTemplateRef.current);

  const applyTemplate = () => {
    setPlan(templatePlan);
    appliedTemplateRef.current = templatePlan;
    setAppliedRows(rows);
    setDirty(false);
  };

  // Dates a filter (weekdays-only / skip-days-with-punches) currently keeps in the plan. A day that becomes
  // skipped while dirty stays in `plan` (so re-enabling the filter brings its edits back) but its lines are
  // hidden from the counts, the editor, and the submit - the plan's effective content follows the filters.
  const plannedDates = useMemo(() => new Set(customDays.filter((day) => day.status === 'planned').map((day) => day.date)), [customDays]);
  const effectivePlan = useMemo(() => plan.filter((p) => plannedDates.has(p.date)), [plan, plannedDates]);

  // No shiftId on custom punches, so distinct dates and distinct source days are the same thing here.
  const dayCount = new Set(effectivePlan.map((p) => p.date)).size;
  const groupedPlan = groupByDate(effectivePlan);
  const editedCount = customDays.filter((day) => day.status === 'planned' && isDayEdited(groupedPlan.get(day.date) ?? [], appliedRows)).length;
  const hasEmptyTime = effectivePlan.some((p) => !HH_MM_RE.test(p.time));
  const overCap = effectivePlan.length > MAX_PUNCHES_PER_REQUEST;
  // Skip-days-with-punches has no punch data outside the loaded window, and the table only
  // reloads the loaded range, so created punches outside it would never show up.
  const outsideLoaded = YYYY_MM_DD_RE.test(from) && YYYY_MM_DD_RE.test(to) && (from < initialRange.from || to > initialRange.to);

  const updateRow = (index: number, patch: Partial<CustomPunchRow>) => setRows(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const removeRow = (index: number) => setRows(rows.filter((_, i) => i !== index));
  const addRow = () => setRows([...rows, { time: '09:00', punchType: 'entry' }]);
  const reset = () => setRows(DEFAULT_ROWS);

  const doSubmit = () => submit({ createdBy, requestedBy: requestedBy || undefined, reason: createdBy === 'employee_realtime' ? undefined : reason || undefined, punches: sortPlan(effectivePlan) });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add custom punches</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField label="From" type="date" size="small" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: initialRange.from, max: initialRange.to }} error={outsideLoaded} />
            <TextField label="To" type="date" size="small" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: initialRange.from, max: initialRange.to }} error={outsideLoaded} />
          </Stack>
          {rows.map((row, index) => (
            <Stack direction="row" spacing={1} alignItems="center" key={index}>
              <TextField label="Time" type="time" size="small" value={row.time} onChange={(e) => updateRow(index, { time: e.target.value })} InputLabelProps={{ shrink: true }} />
              <TextField label="Type" select size="small" value={row.punchType} onChange={(e) => updateRow(index, { punchType: e.target.value as PunchType })} sx={{ minWidth: 140 }}>
                {PUNCH_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
              <IconButton aria-label="Remove row" onClick={() => removeRow(index)}>
                <DeleteIcon />
              </IconButton>
            </Stack>
          ))}
          <Stack direction="row" spacing={1}>
            <Button startIcon={<AddIcon />} onClick={addRow}>
              Add row
            </Button>
            <Button onClick={reset}>Reset to 9–18 with lunch</Button>
            <Button onClick={applyTemplate} disabled={!dirty}>
              Apply template to all days
            </Button>
          </Stack>
          <FormControlLabel control={<Checkbox checked={weekdaysOnly} onChange={(e) => setWeekdaysOnly(e.target.checked)} />} label="Weekdays only (Mon–Fri)" />
          <FormControlLabel control={<Checkbox checked={skipDaysWithPunches} onChange={(e) => setSkipDaysWithPunches(e.target.checked)} />} label="Skip days with punches" />
          <DialogCommonFields createdBy={createdBy} onCreatedByChange={setCreatedBy} reason={reason} onReasonChange={setReason} requestedBy={requestedBy} onRequestedByChange={setRequestedBy} context={context} />
          <Typography variant="overline">
            Plan · {effectivePlan.length} punches on {dayCount} days
            {editedCount > 0 ? ` · ${editedCount} days edited` : ''}
          </Typography>
          {templateChanged && (
            <Typography variant="body2" color="warning.main">
              {TEMPLATE_CHANGED_HINT}
            </Typography>
          )}
          <CustomPlanEditor
            days={customDays}
            plan={effectivePlan}
            rows={appliedRows}
            onChange={(next) => {
              // `next` reflects only the currently visible (planned) lines the editor was given; splice
              // them back into the full plan so lines hidden by a filter are kept, not dropped.
              const hiddenLines = plan.filter((p) => !plannedDates.has(p.date));
              setPlan([...hiddenLines, ...next]);
              setDirty(true);
            }}
          />
          {hasEmptyTime && (
            <Typography variant="body2" color="error">
              {INVALID_TIME_HINT}
            </Typography>
          )}
          {overCap && (
            <Typography variant="body2" color="error">
              Maximum 200 punches per request
            </Typography>
          )}
          {outsideLoaded && (
            <Typography variant="body2" color="warning.main">
              {outsideLoadedHint(initialRange.from, initialRange.to)}
            </Typography>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {partial && (
            <Stack spacing={0.5}>
              <Typography variant="body2">
                Created {partial.created.length}, failed {partial.failed.length}
              </Typography>
              {partial.failed.map((f, i) => (
                <Typography key={i} variant="body2" color="error">
                  {f.date} {f.time} {f.punchType}: {f.error}
                </Typography>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {partial ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={effectivePlan.length === 0 || overCap || outsideLoaded || submitting || requestedByInvalid || hasEmptyTime} startIcon={submitting ? <CircularProgress size={16} /> : undefined} onClick={doSubmit}>
              Create
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddCustomDialog;
